import { CommonModule } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from "@azure/msal-angular";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
} from "@azure/msal-browser";
import {
  DxButtonModule,
  DxLoadPanelModule,
  DxMenuModule,
  DxToolbarModule,
} from "devextreme-angular";
import { DxMenuTypes } from "devextreme-angular/ui/menu";
import { Subject, filter, takeUntil } from "rxjs";
import { environment } from "../environments/environment";
import { GlobalSearchComponent } from "./core/components/global-search/global-search.component";
import { AuthService } from "./core/services/auth.service";
import { SignalService } from "./core/services/loader.service";
import { UserService } from "./core/services/user.service";
import { GetAllActivePEUseCase } from "./domain/use-cases/hr/get-all-active-pe.usecase";
import { AgendaService } from "./presentation/pages/agenda/services/agenda.service";
import { ModalDelegateComponent } from "./presentation/shared/modal-delegate/modal-delegate.component";

interface User {
  rinaId: string;
  userRole: string;
}

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    DxToolbarModule,
    DxMenuModule,
    DxButtonModule,
    DxLoadPanelModule,
    GlobalSearchComponent,
    DxMenuModule,
    ModalDelegateComponent,
  ],
  providers: [],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  userName = "";
  userRole: any = "PE/TC";
  user = signal<User>({ rinaId: "", userRole: "" });
  title = `Is production: ${environment.production}, api: ${environment.apiUrl}`;

  isRcip = false;
  homeRoute = "home";
  profileSettings = [
    { value: 1, name: "Login redirect", onClick: () => this.loginRedirect() },
    { value: 3, name: "Login popup", onClick: () => this.loginPopup() },
    { value: 2, name: "Logout redirect", onClick: () => this.logout() },
    { value: 4, name: "Logout popup", onClick: () => this.logout(true) },
  ];

  isPopupVisible: boolean = false;

  showSubmenuConfig = {
    name: "onClick" as DxMenuTypes.SubmenuShowMode,
    delay: { show: 0, hide: 300 },
  };

  reportList = [
    {
      id: 1,
      icon: "datausage",
      items: [
        {
          id: 1,
          value: "d4c55368546b76ab36a4",
          name: "My View - DL",
          onClick: (e: any) => this.getReport(e, "Discipline Leader View"),
        },
        {
          id: 2,
          value: "d1e7e3e43c65b9a087b4",
          name: "My View - Planner",
          onClick: (e: any) => this.getReport(e, "Planner View"),
        },
        {
          id: 3,
          value: "",
          name: "My View - Evaluation Report",
          onClick: (e: any) => this.getReportEvaluation(e, "Evaluation Report"),
        },
      ],
    },
  ];
  // AUTH STUFF
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private router: Router,
    public signalService: SignalService,
    private auth: AuthService,
    private agendaService: AgendaService,
    private route: ActivatedRoute,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService,
    private getAllResources: GetAllActivePEUseCase,
  ) {}

  ngOnInit(): void {
    const hostname = window.location.hostname.toLowerCase();
    this.isRcip = hostname.includes('nam') || hostname.includes('rcip');
    this.homeRoute = (hostname.includes('smart') && !this.isRcip) ? 'agenda' : 'home';
    if (this.isRcip) {
      document.title = 'RCIP';
    }

    // AUTH STUFF
    this.auth.setup();
    this.msalAuthService.handleRedirectObservable().subscribe();

    // Save target URL before MSAL redirect so we can restore it after auth
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_START),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        const hash = window.location.hash;
        if (hash && hash !== '#/' && hash !== '#') {
          sessionStorage.setItem('msal.redirect.target', window.location.href);
        }
      });

    // Navigate back to the original URL after successful login
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        const savedUrl = sessionStorage.getItem('msal.redirect.target');
        if (savedUrl) {
          sessionStorage.removeItem('msal.redirect.target');
          const hashIndex = savedUrl.indexOf('#');
          const targetPath = hashIndex >= 0 ? savedUrl.substring(hashIndex + 1) : null;
          if (targetPath && targetPath !== '/' && targetPath !== '') {
            this.router.navigateByUrl(targetPath);
          }
        }
      });

    this.setLoginDisplay();

    this.msalAuthService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalAuthService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.redirectBasedOnHostname();
      });

    let usr = this.auth.getProfile();

    if (usr && usr.name) {
      this.userName = usr.name;
      let rinaUser = usr.username ?? "CMT";
      this.login();
      let subStr = rinaUser.indexOf("@");
      if (subStr > 0) rinaUser = rinaUser.substring(0, subStr);
      this.agendaService.getUserRole(rinaUser).subscribe((res) => {
        this.userService.updateProfile(
          res.userRole.roleDescription,
          res.userRole.userRole,
          res.userRole.resourceId,
          res.userRole.practiceId,
          res.userRole.disciplineId,
          usr?.name ?? "", 
        );
        this.userRole = res.userRole.roleDescription;
        this.user.set({ rinaId: rinaUser, userRole: this.userRole });
      });
    }
  }

  redirectBasedOnHostname() {
    const currentUrl = this.router.url;
    const hostname = window.location.hostname.toLowerCase();
    const hostname2 = window.location.href.toLowerCase();
    console.log(`[HomeRedirect] hostname=${hostname}, currentUrl=${currentUrl}, hostname2=${hostname2}`);

    // Check the browser hash for a deep link that the router hasn't processed yet
    const hash = window.location.hash;
    const hashPath = hash ? hash.replace('#', '') : '';

    // Only redirect if we are truly on the default landing page (no deep link pending)
    if (currentUrl !== '/home' && currentUrl !== '/') return;
    if (hashPath && hashPath !== '/' && hashPath !== '/home' && hashPath !== '') {
      console.log(`[HomeRedirect] Deep link detected: ${hashPath}, skipping redirect`);
      return;
    }

    if (hostname.includes('smart') && !hostname.includes('nam') && !hostname.includes('rcip')) {
      console.log('[HomeRedirect] → /agenda');
      this.router.navigate(['/agenda']);
    } else if (hostname.includes('nam') || hostname.includes('rcip')) {
      console.log('[HomeRedirect] → /home (real-cost)');
      this.router.navigate(['/home']);
    }
    // else: stay on /home (default fallback)
  }

  // If the user is logged in, present the user with a "logged in" experience
  setLoginDisplay() {
    this.loginDisplay =
      this.msalAuthService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalAuthService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.msalAuthService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalAuthService.instance.getAllAccounts();
      this.msalAuthService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.msalAuthService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalAuthService.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.msalAuthService
        .loginPopup({
          ...this.msalGuardConfig.authRequest,
          redirectUri: "",
        } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.msalAuthService.instance.setActiveAccount(response.account);
        });
    } else {
      this.msalAuthService
        .loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.msalAuthService.instance.setActiveAccount(response.account);
        });
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.msalAuthService.logoutPopup({
        mainWindowRedirectUri: "/",
      });
    } else {
      this.msalAuthService.logoutRedirect();
    }
  }

  // Log the user in and redirect them if MSAL provides a redirect URI otherwise go to the default URI
  login() {
    if (this.msalGuardConfig.authRequest) {
      this.msalAuthService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        redirectUri: "",
      } as RedirectRequest);
    } else {
      this.msalAuthService.loginRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  getReport(e: any, title: string) {
    if (e && e.itemData && e.itemData.value) {
      this.signalService.titleSignal.set(title);
      this.router.navigateByUrl(`/report/${e.itemData.value}`);
    }
  }

  getReportEvaluation(e: any, title: string) {
    this.signalService.titleSignal.set(title);
    this.router.navigateByUrl(`/evaluation/report`);
  }

  openDelegateModal(): void {
    this.isPopupVisible = true;
  }

  onCloseModal(e: any) {
    this.isPopupVisible = false;
  }

  canDelegate(role: string) {
    return ["Admin", "SuperUser", "PL", "DL", "PE"].includes(role);
  }
}
